/**
 * Settings > Organization > Permissions: who besides owners and admins may
 * write the firm's Firm Knowledge. One row per capability, one switch per
 * role; every switch starts on. Owners and admins always hold everything, so
 * their column is shown locked. A switch saves as soon as it flips.
 */
import { Lock } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import type {
  FirmCapability,
  FirmConfigurableRole,
  FirmPermissionsDTO
} from "@/api/types/lawFirm";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { updatedLabel } from "./FirmInstructionsSection";

type Props = {
  lawFirmId: string | null;
};

const Divider = () => <div className="h-px w-full bg-fill-1" />;

/** Every role column shares one width so the header lines up with the switches. */
const SWITCH_CELL = "flex w-24 justify-center";
const HEADER_CELL =
  "w-24 text-center text-caption-bold font-caption-bold text-subtext-color";

const withSwitch = (
  data: FirmPermissionsDTO,
  capability: FirmCapability,
  role: FirmConfigurableRole,
  on: boolean
): FirmPermissionsDTO => ({
  ...data,
  matrix: {
    ...data.matrix,
    [capability]: { ...data.matrix[capability], [role]: on }
  }
});

export const PermissionsTab: FC<Props> = ({ lawFirmId }) => {
  const [data, setData] = useState<FirmPermissionsDTO | null>(null);
  const [loading, setLoading] = useState(Boolean(lawFirmId));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!lawFirmId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    LawFirmsAPI.getPermissions(lawFirmId)
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Could not load permissions."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lawFirmId]);

  const footer = useMemo(() => updatedLabel(data), [data]);

  const toggle = async (
    capability: FirmCapability,
    role: FirmConfigurableRole,
    on: boolean
  ) => {
    if (!lawFirmId || !data) return;
    const previous = data;
    setData(withSwitch(data, capability, role, on));
    setSaving(`${capability}:${role}`);
    try {
      const updated = await LawFirmsAPI.updatePermissions(lawFirmId, {
        [capability]: { [role]: on }
      });
      setData(updated);
    } catch (err) {
      setData(previous);
      toast.error(
        err instanceof Error ? err.message : "Could not save permissions."
      );
    } finally {
      setSaving(null);
    }
  };

  const columns = data?.roles ?? [];

  return (
    <TooltipProvider>
      <section className="mt-6 w-full" data-testid="permissions-tab">
        {error ? (
          <>
            <Divider />
            <div className="px-6 py-6">
              <span className="text-body font-body text-subtext-color">
                {error}
              </span>
            </div>
            <Divider />
          </>
        ) : loading ? (
          <>
            <Divider />
            {[0, 1].map((item) => (
              <div key={item}>
                <div className="flex w-full items-center justify-between gap-6 px-6 py-4">
                  <div className="flex min-w-0 flex-col gap-2">
                    <div className="h-4 w-24 rounded bg-fill-1" />
                    <div className="h-3 w-56 rounded bg-fill-1" />
                  </div>
                  <div className="h-5 w-64 rounded bg-fill-1" />
                </div>
                <Divider />
              </div>
            ))}
          </>
        ) : data ? (
          <div data-testid="permissions-table">
            <div className="flex w-full items-center justify-end gap-6 px-6 pb-2">
              <span className={HEADER_CELL}>Owner &amp; Admin</span>
              {columns.map((role) => (
                <span key={role.key} className={HEADER_CELL}>
                  {role.label}
                </span>
              ))}
            </div>
            <Divider />
            {data.capabilities.map((capability) => (
              <div key={capability.key}>
                <div className="flex w-full items-center justify-between gap-6 px-6 py-4">
                  <div className="flex min-w-0 flex-col items-start gap-1">
                    <span className="truncate text-body-bold font-body-bold text-default-font">
                      {capability.label}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {capability.description}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-6">
                    <div className={SWITCH_CELL}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1.5">
                            <Switch
                              checked
                              disabled
                              aria-label={`${capability.label}: owners and admins`}
                            />
                            <Lock className="size-3.5 text-subtext-color" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>Always</TooltipContent>
                      </Tooltip>
                    </div>
                    {columns.map((role) => (
                      <div key={role.key} className={SWITCH_CELL}>
                        <Switch
                          checked={data.matrix[capability.key][role.key]}
                          disabled={saving !== null}
                          onCheckedChange={(on) =>
                            void toggle(capability.key, role.key, on)
                          }
                          aria-label={`${capability.label}: ${role.label}`}
                          data-testid={`permission-${capability.key}-${role.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <Divider />
              </div>
            ))}
            {data.updated_at ? (
              <div className="px-6 pt-3">
                <span
                  className="text-caption font-caption text-subtext-color"
                  data-testid="permissions-footer"
                >
                  {footer}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </TooltipProvider>
  );
};
